import Component from '@bythepixel/component-loader';

const DEFAULTS = {
  gridColumnCount: '[data-grid-col-size]',
  gridRowCount: '[data-grid-row-size]',
  colorSquareItem: '[data-color-square-item]',
  containerSelector: '[data-comp-color-square-grid-container]',
};

/**
 * ColorSquareGrid component to handle creating a grid of size x,y of squares filled with solid colors.
 * This component also observes the container of the grid for viewport changes and reveals or hides the squares
 * accordingly
 *
 * NOTE:: For the BTPV2 website we actually want to be specific with the arrangement of the colored squares, so we
 * aren't using the `mount` function to init the script to generate a grid of random colors. Instead, we are only
 * using the observer functionality of this class to watch for viewport changes and reveal predetermined squares.
 */
export default class ColorSquareGrid extends Component {
  static selector = '[data-comp-color-square-grid-container]';

  constructor(containerEl, options = DEFAULTS) {
    super(containerEl, options);
    this.gridColumnCount = this.$container.dataset.gridColSize;
    this.gridRowCount = this.$container.dataset.gridRowSize;
    this.colors = [
      'animated-noise',
      'gradient-1',
      'gradient-2',
      'gradient-3',
      'black',
      'pink',
      'blue',
      'orange',
      'green',
    ];
    this.observeContainer(this.$container);
  }

  /**
   *  Generate a Grid of items given a Column Count and a Row Count
   * @param x | column
   * @param y | row
   * @returns {*[]}
   */
  createGrid = (x, y) => {
    const grid = [];
    // Initialize the grid with random colors
    for (let i = 0; i < y; i += 1) {
      grid[i] = [];
      for (let j = 0; j < x; j += 1) {
        grid[i][j] = this.getRandomColor();
      }
    }
    // Adjust colors to ensure no adjacent squares have the same color
    for (let i = 0; i < y; i += 1) {
      for (let j = 0; j < x; j += 1) {
        const adjacentColors = this.getAdjacentColors(grid, i, j);
        const availableColors = this.colors.filter(color => !adjacentColors.includes(color));
        grid[i][j] = this.getRandomColorFromOptions(availableColors);
      }
    }
    return grid;
  }

  getRandomColor = () => this.colors[Math.floor(Math.random() * this.colors.length)];

  getAdjacentColors = (grid, row, col) => {
    const adjacentColors = [];
    const directions = [
      [-1, 0], // Up
      [0, 1], // Right
      [1, 0], // Down
      [0, -1], // Left
    ];

    for (let i = 0; i < directions.length; i += 1) {
      const [dx, dy] = directions[i];
      const newRow = row + dx;
      const newCol = col + dy;
      if (this.isValidPosition(grid, newRow, newCol)) {
        adjacentColors.push(grid[newRow][newCol]);
      }
    }
    return adjacentColors;
  }

  isValidPosition = (grid, row, col) => row >= 0 && row < grid.length && col >= 0 && col < grid[0].length;

  getRandomColorFromOptions = (options) => options[Math.floor(Math.random() * options.length)];

  observeContainer = (containerElement) => {
    let previousY = 0;
    let previousRatio = 0;

    const colorGridObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const currentY = entry.boundingClientRect.y;
        const currentRatio = entry.intersectionRatio;
        const { isIntersecting } = entry;
        // Scrolling down/up
        if (currentY < previousY) {
          if (currentRatio > previousRatio && isIntersecting) {
            this.showSquares(); // Scrolling down and entering viewport
          }
        } else if (currentY > previousY && isIntersecting) {
          this.showSquares(); // Scrolling Up and entering viewport
        }
        previousY = currentY;
        previousRatio = currentRatio;
      });
    }, { threshold: [0.5, 1] });

    colorGridObserver.observe(containerElement);
  }

  showSquares = (reveal = true) => {
    const squares = this.$container.querySelectorAll(this.$options.colorSquareItem);
    // randomize the squares and apply the class to reveal / hide them
    Array.from(squares)
      .map(el => ({ el, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .forEach((square) => (reveal ? square.el.classList.add('reveal') : square.el.classList.remove('reveal')));
  }

  mount = () => {
    const grid = this.createGrid(this.gridColumnCount, this.gridRowCount);
    grid.forEach((gridRow) => {
      gridRow.forEach((item, index) => {
        const square = document.createElement('div');
        square.classList.add(`color-square-grid__item-${gridRow[index]}`);
        square.classList.add('grid-item');
        square.setAttribute('data-color-square-item', '');
        this.$container.appendChild(square);
      });
    });
    this.$container.style.gridTemplateColumns = `repeat(${this.gridColumnCount}, 1fr)`;
  }
}
